import React, { useCallback, useEffect, useRef, useState } from 'react';
import GoldCoin from '../assets/images/gold-coin.png';

const GAME_AREA = {
  width: 260,
  height: 140,
  padding: 10,
  groundHeight: 0,
  playerSize: 18,
  cubeSize: 18,
  playerX: 40
};

const GAME_CONFIG = {
  gravity: 1800,
  jumpVelocity: 520,
  speed: 150,
  spawnIntervalMs: 1200,
  autoJumpIntervalMs: 900,
  spinSpeed: 720
};

const COIN_CONFIG = {
  size: 14,
  offset: 4,
  fadeDurationMs: 150
};

const OBSTACLE_PATTERNS = [
  { name: 'single', blocks: [{ x: 0, y: 0 }] },
  { name: 'tall-2', blocks: [{ x: 0, y: 0 }, { x: 0, y: 1 }] },
  { name: 'short-platform', blocks: [{ x: 0, y: 1 }, { x: 1, y: 1 }] },
  { name: 'long-platform', blocks: [{ x: 0, y: 1 }, { x: 1, y: 1 }, { x: 2, y: 1 }] },
  { name: 'stair-2', blocks: [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 2, y: 1 }, { x: 3, y: 1 }] },
  { name: 'stair-3', blocks: [
    { x: 0, y: 0 }, { x: 1, y: 0 },
    { x: 2, y: 1 }, { x: 3, y: 1 }, { x: 4, y: 1 },
    { x: 5, y: 2 }, { x: 6, y: 2 }
  ] }
];

const getPatternSize = (blocks) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return { widthUnits: 1, heightUnits: 1 };
  }
  const widthUnits = Math.max(...blocks.map((block) => block.x)) + 1;
  const heightUnits = Math.max(...blocks.map((block) => block.y)) + 1;
  return { widthUnits, heightUnits };
};

const pickObstaclePattern = (score) => {
  const safeScore = Number.isFinite(score) ? score : 0;
  if (safeScore < 5) {
    return OBSTACLE_PATTERNS[0];
  }

  if (safeScore < 25) {
    const weightedPool = [
      OBSTACLE_PATTERNS[0],
      OBSTACLE_PATTERNS[0],
      OBSTACLE_PATTERNS[1],
      OBSTACLE_PATTERNS[2],
      OBSTACLE_PATTERNS[3]
    ];
    return weightedPool[Math.floor(Math.random() * weightedPool.length)];
  }

  const weightedPool = [
    OBSTACLE_PATTERNS[0],
    OBSTACLE_PATTERNS[0],
    OBSTACLE_PATTERNS[1],
    OBSTACLE_PATTERNS[2],
    OBSTACLE_PATTERNS[3],
    OBSTACLE_PATTERNS[4],
    OBSTACLE_PATTERNS[5]
  ];
  return weightedPool[Math.floor(Math.random() * weightedPool.length)];
};

const getSpawnDelayForPattern = (pattern) => {
  if (!pattern?.name) return 0;
  if (pattern.name.startsWith('stair')) {
    return 450;
  }
  return 0;
};

const getCoinBlocksForPattern = (pattern) => {
  if (!pattern?.name) return [];
  if (pattern.name === 'tall-2') {
    return [{ x: 0, y: 1 }];
  }
  if (pattern.name.includes('platform')) {
    return (pattern.blocks || []).filter((block) => block.y >= 1);
  }
  return [];
};

export default function TriangleJump() {
  const [gameScore, setGameScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [isAwaitingRestart, setIsAwaitingRestart] = useState(false);
  const [newHighScoreValue, setNewHighScoreValue] = useState(null);
  const [playerY, setPlayerY] = useState(0);
  const [playerRotation, setPlayerRotation] = useState(0);
  const [obstacles, setObstacles] = useState([]);
  const [hasUserJumped, setHasUserJumped] = useState(false);
  const [gameWidth, setGameWidth] = useState(GAME_AREA.width);
  const [showJumpHint, setShowJumpHint] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [scoreFlashKey, setScoreFlashKey] = useState(0);

  const playerYRef = useRef(0);
  const velocityRef = useRef(0);
  const rotationRef = useRef(0);
  const obstaclesRef = useRef([]);
  const scoreRef = useRef(0);
  const highScoreRef = useRef(0);
  const lastTickRef = useRef(0);
  const lastSpawnRef = useRef(0);
  const lastAutoJumpRef = useRef(0);
  const hasUserJumpedRef = useRef(false);
  const gameWidthRef = useRef(GAME_AREA.width);
  const gameAreaRef = useRef(null);
  const isAwaitingRestartRef = useRef(false);
  const isGroundedRef = useRef(true);

  const handleJump = useCallback((source) => {
    if (isAwaitingRestartRef.current) return;
    const onGround = isGroundedRef.current;
    if (!onGround) return;
    velocityRef.current = GAME_CONFIG.jumpVelocity;
    if (source === 'user') {
      if (!hasUserJumpedRef.current) {
        hasUserJumpedRef.current = true;
        setHasUserJumped(true);
      }
    }
  }, []);

  useEffect(() => {
    scoreRef.current = gameScore;
  }, [gameScore]);

  useEffect(() => {
    highScoreRef.current = highScore;
  }, [highScore]);

  useEffect(() => {
    isAwaitingRestartRef.current = isAwaitingRestart;
  }, [isAwaitingRestart]);

  useEffect(() => {
    obstaclesRef.current = obstacles;
  }, [obstacles]);

  useEffect(() => {
    playerYRef.current = playerY;
  }, [playerY]);

  useEffect(() => {
    rotationRef.current = playerRotation;
  }, [playerRotation]);

  useEffect(() => {
    hasUserJumpedRef.current = hasUserJumped;
  }, [hasUserJumped]);

  useEffect(() => {
    const updateTouch = () => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        setIsTouchDevice(false);
        return;
      }
      const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
      const noHover = window.matchMedia('(hover: none)').matches;
      setIsTouchDevice(coarsePointer || noHover);
    };
    updateTouch();
    window.addEventListener('resize', updateTouch);
    return () => window.removeEventListener('resize', updateTouch);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes triangleJumpPulse {
        0%, 100% { transform: scale(1); opacity: 0.9; }
        50% { transform: scale(1.04); opacity: 1; }
      }
      @keyframes triangleJumpCoinFloat {
        0% { transform: translateY(0) scale(1); opacity: 1; }
        100% { transform: translateY(-14px) scale(2); opacity: 0; }
      }
      @keyframes triangleJumpScoreFlash {
        0% { background: rgba(17, 24, 39, 0.65); transform: scale(1); }
        55% { background: #F59E0B; transform: scale(1.08); }
        100% { background: rgba(17, 24, 39, 0.65); transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (!hasUserJumped) {
      setShowJumpHint(false);
      return;
    }
    setShowJumpHint(true);
    if (isTouchDevice) return;
    const timeoutId = setTimeout(() => {
      setShowJumpHint(false);
    }, 3500);
    return () => clearTimeout(timeoutId);
  }, [hasUserJumped, isTouchDevice]);

  useEffect(() => {
    gameWidthRef.current = gameWidth;
  }, [gameWidth]);

  const resetGameState = useCallback((timestamp) => {
    setGameScore(0);
    setPlayerY(0);
    setPlayerRotation(0);
    setObstacles([]);
    scoreRef.current = 0;
    playerYRef.current = 0;
    velocityRef.current = 0;
    rotationRef.current = 0;
    obstaclesRef.current = [];
    lastTickRef.current = 0;
    lastSpawnRef.current = timestamp || 0;
    lastAutoJumpRef.current = timestamp || 0;
  }, []);

  const handleGameTap = useCallback(() => {
    if (isAwaitingRestartRef.current) {
      isAwaitingRestartRef.current = false;
      setIsAwaitingRestart(false);
      setNewHighScoreValue(null);
      resetGameState();
      return;
    }
    handleJump('user');
  }, [handleJump, resetGameState]);

  useEffect(() => {
    if (!gameAreaRef.current) return;
    const updateWidth = () => {
      const nextWidth = gameAreaRef.current?.clientWidth || GAME_AREA.width;
      setGameWidth(Math.max(180, nextWidth));
    };
    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth);
      observer.observe(gameAreaRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    let animationId;
    const tick = (timestamp) => {
      if (!lastTickRef.current) {
        lastTickRef.current = timestamp;
        lastSpawnRef.current = timestamp;
        lastAutoJumpRef.current = timestamp;
      }

      if (isAwaitingRestartRef.current) {
        animationId = requestAnimationFrame(tick);
        return;
      }

      const dt = Math.min(0.05, (timestamp - lastTickRef.current) / 1000);
      lastTickRef.current = timestamp;

      const shouldAutoJump = !hasUserJumpedRef.current;
      if (shouldAutoJump && timestamp - lastAutoJumpRef.current >= GAME_CONFIG.autoJumpIntervalMs) {
        handleJump('auto');
        lastAutoJumpRef.current = timestamp;
      }

      const spawnDue = timestamp - lastSpawnRef.current >= GAME_CONFIG.spawnIntervalMs;
      const currentWidth = gameWidthRef.current;
      const speedMultiplier = 1 + Math.floor(scoreRef.current / 5) * 0.05;
      const currentSpeed = GAME_CONFIG.speed * speedMultiplier;
      let nextObstacles = obstaclesRef.current.map((obstacle) => ({
        ...obstacle,
        x: obstacle.x - currentSpeed * dt
      }));

      if (spawnDue) {
        const spawnX = currentWidth + GAME_AREA.cubeSize + 10;
        const pattern = pickObstaclePattern(scoreRef.current);
        const { widthUnits, heightUnits } = getPatternSize(pattern.blocks);
        const extraDelayMs = getSpawnDelayForPattern(pattern);
        const coinBlocks = getCoinBlocksForPattern(pattern);
        nextObstacles = [
          ...nextObstacles,
          {
            id: `${timestamp}-${Math.random()}`,
            x: spawnX,
            blocks: pattern.blocks,
            widthUnits,
            heightUnits,
            coins: coinBlocks.map((block, index) => ({
              id: `${timestamp}-${index}-${Math.random()}`,
              blockX: block.x,
              blockY: block.y,
              collected: false,
              collectedAt: null
            }))
          }
        ];
        lastSpawnRef.current = timestamp + extraDelayMs;
      }

      let passedCount = 0;
      let scoreDelta = 0;
      nextObstacles = nextObstacles.filter((obstacle) => {
        const widthUnits = obstacle.widthUnits || 1;
        if (obstacle.x + widthUnits * GAME_AREA.cubeSize < 0) {
          passedCount += 1;
          return false;
        }
        return true;
      });

      if (passedCount > 0 && hasUserJumpedRef.current) {
        scoreDelta += passedCount;
      }

      let velocity = velocityRef.current - GAME_CONFIG.gravity * dt;
      const prevPlayerY = playerYRef.current;
      let nextPlayerY = prevPlayerY + velocity * dt;

      const playerLeft = GAME_AREA.playerX;
      const playerRight = playerLeft + GAME_AREA.playerSize;
      let landedOnPlatform = false;
      let landingY = null;

      for (const obstacle of nextObstacles) {
        const blocks = obstacle.blocks || [{ x: 0, y: 0 }];
        for (const block of blocks) {
          const blockLeft = obstacle.x + block.x * GAME_AREA.cubeSize;
          const blockRight = blockLeft + GAME_AREA.cubeSize;
          const overlapX = playerRight > blockLeft && playerLeft < blockRight;
          if (!overlapX) continue;

          const blockTop = (block.y + 1) * GAME_AREA.cubeSize;
          const wasAbove = prevPlayerY >= blockTop - 0.5;
          const isDescending = velocity <= 0;

          if ((isDescending && wasAbove && nextPlayerY <= blockTop) ||
              (Math.abs(prevPlayerY - blockTop) <= 0.5 && nextPlayerY <= blockTop)) {
            if (landingY === null || blockTop > landingY) {
              landingY = blockTop;
            }
          }
        }
      }

      if (landingY !== null) {
        nextPlayerY = landingY;
        if (velocity < 0) velocity = 0;
        landedOnPlatform = true;
      } else if (nextPlayerY <= 0) {
        nextPlayerY = 0;
        if (velocity < 0) velocity = 0;
      }

      velocityRef.current = velocity;
      playerYRef.current = nextPlayerY;
      setPlayerY(nextPlayerY);

      let nextRotation = rotationRef.current;
      const isGrounded = landedOnPlatform || nextPlayerY <= 0.5;
      if (!isGrounded) {
        nextRotation = (nextRotation + GAME_CONFIG.spinSpeed * dt) % 360;
      } else {
        nextRotation = 0;
      }
      rotationRef.current = nextRotation;
      setPlayerRotation(nextRotation);
      isGroundedRef.current = isGrounded;

      const playerRect = {
        x: GAME_AREA.playerX,
        y: nextPlayerY,
        size: GAME_AREA.playerSize
      };

      let collectedCount = 0;
      nextObstacles = nextObstacles.map((obstacle) => {
        if (!obstacle.coins?.length) return obstacle;
        let coinsUpdated = false;
        const coins = obstacle.coins.map((coin) => {
          if (coin.collected) return coin;
          const coinLeft =
            obstacle.x + coin.blockX * GAME_AREA.cubeSize + (GAME_AREA.cubeSize - COIN_CONFIG.size) / 2;
          const coinBottom =
            GAME_AREA.groundHeight + (coin.blockY + 1) * GAME_AREA.cubeSize + COIN_CONFIG.offset;
          const coinRect = {
            x: coinLeft,
            y: coinBottom,
            size: COIN_CONFIG.size
          };
          const overlapX =
            playerRect.x < coinRect.x + coinRect.size &&
            playerRect.x + playerRect.size > coinRect.x;
          const overlapY =
            playerRect.y < coinRect.y + coinRect.size &&
            playerRect.y + playerRect.size > coinRect.y;
          if (overlapX && overlapY) {
            coinsUpdated = true;
            collectedCount += 1;
            return { ...coin, collected: true, collectedAt: timestamp };
          }
          return coin;
        });
        return coinsUpdated ? { ...obstacle, coins } : obstacle;
      });

      if (collectedCount > 0 && hasUserJumpedRef.current) {
        scoreDelta += collectedCount;
      }
      if (collectedCount > 0) {
        setScoreFlashKey((prev) => prev + 1);
      }

      if (scoreDelta > 0) {
        const nextScore = scoreRef.current + scoreDelta;
        scoreRef.current = nextScore;
        setGameScore(nextScore);
      }

      const collision = nextObstacles.some((obstacle) => {
        const blocks = obstacle.blocks || [{ x: 0, y: 0 }];
        return blocks.some((block) => {
          const blockRect = {
            x: obstacle.x + block.x * GAME_AREA.cubeSize,
            y: block.y * GAME_AREA.cubeSize,
            size: GAME_AREA.cubeSize
          };
          const overlapX =
            playerRect.x < blockRect.x + blockRect.size &&
            playerRect.x + playerRect.size > blockRect.x;
          const overlapY =
            playerRect.y < blockRect.y + blockRect.size &&
            playerRect.y + playerRect.size > blockRect.y;
          return overlapX && overlapY;
        });
      });

      if (collision) {
        const finalScore = scoreRef.current;
        if (finalScore > highScoreRef.current) {
          setHighScore(finalScore);
          setNewHighScoreValue(finalScore);
          isAwaitingRestartRef.current = true;
          setIsAwaitingRestart(true);
        } else {
          resetGameState(timestamp);
        }
      } else {
        obstaclesRef.current = nextObstacles;
        setObstacles(nextObstacles);
      }

      animationId = requestAnimationFrame(tick);
    };

    animationId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animationId);
  }, [handleJump, resetGameState]);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === ' ' || event.key === 'ArrowUp' || event.key === 'Spacebar') {
            event.preventDefault();
            handleGameTap();
          }
        }}
        onClick={handleGameTap}
        style={{
          width: '100%',
          height: `${GAME_AREA.height}px`,
          borderRadius: 0,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          position: 'relative',
          overflow: 'hidden',
          margin: 0,
          cursor: 'pointer'
        }}
        aria-label="Spielfeld"
        ref={gameAreaRef}
      >
        {!hasUserJumped && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.95rem',
              fontWeight: 800,
              color: '#111827',
              background: 'transparent',
              textShadow: '0 1px 2px rgba(255,255,255,0.6)',
              pointerEvents: 'none',
              animation: 'triangleJumpPulse 1.4s ease-in-out infinite'
            }}
          >
            Zum Zeitvertreib klicken
          </div>
        )}
        {hasUserJumped && !isAwaitingRestart && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '12px',
              transform: 'translateX(-50%)',
              fontSize: '0.8rem',
              fontWeight: 700,
              color: '#4B5563',
              background: 'rgba(240, 253, 244, 0.96)',
              padding: '4px 10px',
              borderRadius: '999px',
              border: '1px solid rgba(187, 247, 208, 0.9)',
              pointerEvents: 'none',
              opacity: showJumpHint ? 1 : 0,
              transition: 'opacity 0.6s ease',
              textAlign: 'center'
            }}
          >
            {isTouchDevice
              ? 'Tippen, um zu springen'
              : 'Klicken oder Leertaste drücken, um zu springen'}
          </div>
        )}
        <div
          key={`score-${scoreFlashKey}`}
          style={{
            position: 'absolute',
            right: '10px',
            top: '10px',
            fontSize: '0.9rem',
            fontWeight: 900,
            color: '#FFFFFF',
            background: 'rgba(17, 24, 39, 0.65)',
            padding: '6px 10px',
            borderRadius: '999px',
            letterSpacing: '0.02em',
            transformOrigin: 'center',
            animation: scoreFlashKey > 0 ? 'triangleJumpScoreFlash 260ms ease-out' : 'none'
          }}
          aria-live="polite"
        >
          {gameScore}
        </div>
        {isAwaitingRestart && newHighScoreValue !== null && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              background: 'rgba(17, 24, 39, 0.35)',
              color: '#FFFFFF',
              fontWeight: 900,
              fontSize: '1rem',
              textAlign: 'center',
              gap: '6px',
              pointerEvents: 'none'
            }}
          >
            <div style={{ fontSize: '1.1rem' }}>Neuer Highscore: {newHighScoreValue}</div>
            <div style={{ fontSize: '0.8rem', fontWeight: 700, opacity: 0.9 }}>
              Tippen, um weiterzuspielen
            </div>
          </div>
        )}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: '2px',
            bottom: `${GAME_AREA.groundHeight}px`,
            background: '#D1D5DB'
          }}
        />
        {obstacles.map((obstacle) =>
          (obstacle.blocks || [{ x: 0, y: 0 }]).map((block, idx) => (
            <div
              key={`${obstacle.id}-${idx}`}
              style={{
                position: 'absolute',
                left: `${obstacle.x + block.x * GAME_AREA.cubeSize}px`,
                bottom: `${GAME_AREA.groundHeight + block.y * GAME_AREA.cubeSize}px`,
                width: `${GAME_AREA.cubeSize}px`,
                height: `${GAME_AREA.cubeSize}px`,
                background: '#111827',
                borderRadius: '4px'
              }}
              aria-hidden="true"
            />
          ))
        )}
        {obstacles.flatMap((obstacle) =>
          (obstacle.coins || []).map((coin, idx) => {
            const isCollected = coin.collected;
            return (
              <div
                key={`${obstacle.id}-coin-${idx}`}
                style={{
                  position: 'absolute',
                  left: `${obstacle.x + coin.blockX * GAME_AREA.cubeSize + (GAME_AREA.cubeSize - COIN_CONFIG.size) / 2}px`,
                  bottom: `${GAME_AREA.groundHeight + (coin.blockY + 1) * GAME_AREA.cubeSize + COIN_CONFIG.offset}px`,
                  width: `${COIN_CONFIG.size}px`,
                  height: `${COIN_CONFIG.size}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isCollected ? '0.7rem' : '0.9rem',
                  fontWeight: 800,
                  color: isCollected ? '#F59E0B' : '#FBBF24',
                  lineHeight: 1,
                  transformOrigin: 'center',
                  pointerEvents: 'none',
                  animation: isCollected
                    ? `triangleJumpCoinFloat ${COIN_CONFIG.fadeDurationMs}ms ease-out forwards`
                    : 'none'
                }}
                aria-hidden="true"
              >
                {isCollected ? (
                  '+1'
                ) : (
                  <img
                    src={GoldCoin}
                    alt=""
                    style={{ width: '100%', height: '100%', display: 'block' }}
                    aria-hidden="true"
                  />
                )}
              </div>
            );
          })
        )}
        <div
          style={{
            position: 'absolute',
            left: `${GAME_AREA.playerX}px`,
            bottom: `${GAME_AREA.groundHeight + playerY}px`,
            width: `${GAME_AREA.playerSize}px`,
            height: `${GAME_AREA.playerSize}px`,
            background: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
            clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
            transform: `rotate(${playerRotation}deg)`,
            transformOrigin: '50% 50%'
          }}
          aria-label="Spieler"
        />
      </div>
    </>
  );
}
